import { MapPin, Phone, Clock, MessageCircle } from "lucide-react";
import {
  ADDRESS,
  HOURS,
  NAV_LINKS,
  PHONE_PRIMARY_DISPLAY,
  PHONE_PRIMARY_TEL,
  PHONE_ALT_DISPLAY,
  PHONE_ALT_TEL,
  openWhatsApp,
  GENERAL_WHATSAPP_MESSAGE,
} from "@/lib/constants";
import { scrollToId } from "@/lib/lenis";
import { Reveal } from "@/components/Reveal";

export default function FooterBottom() {
  return (
    <div
      data-testid="footer-bottom"
      className="mt-20 border-t border-[#D4AF37]/15 bg-[#0E1420]"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-16 sm:grid-cols-2 sm:px-8 lg:grid-cols-4 lg:py-20">
        <Reveal>
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display text-3xl font-semibold leading-none tracking-tight text-[#F9F6F0]">
                R<span className="text-[#D4AF37]">&amp;</span>J
              </span>
              <span className="h-7 w-px bg-[#D4AF37]/40" />
              <span className="text-[9px] font-medium uppercase leading-[1.6] tracking-[0.32em] text-[#A8B0BC]">
                Dry
                <br />
                Cleaners
              </span>
            </div>
            <p className="mt-6 max-w-xs text-sm font-light leading-relaxed tracking-wide text-[#A8B0BC]">
              Premium dry cleaning, laundry and garment care with pickup &amp;
              delivery across Yiewsley, West Drayton and the surrounding area.
            </p>
            <button
              data-testid="footer-whatsapp-btn"
              onClick={() => openWhatsApp(GENERAL_WHATSAPP_MESSAGE)}
              className="mt-8 flex items-center gap-2 border border-[#D4AF37]/40 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.25em] text-[#D4AF37] transition-[background-color,border-color] duration-300 hover:border-[#D4AF37]/80 hover:bg-[#D4AF37]/10"
            >
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
              Chat on WhatsApp
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div>
            <h3 className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#D4AF37]">
              Contact
            </h3>
            <ul className="mt-7 space-y-5 text-sm font-light tracking-wide text-[#A8B0BC]">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                <span data-testid="footer-address">{ADDRESS}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                <span>
                  <a
                    data-testid="footer-phone-primary"
                    href={`tel:${PHONE_PRIMARY_TEL}`}
                    className="transition-colors duration-300 hover:text-[#F3E5AB]"
                  >
                    {PHONE_PRIMARY_DISPLAY}
                  </a>
                  <span className="mt-1 block text-xs text-[#A8B0BC]/70">
                    WhatsApp &amp; Calls
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                <span>
                  <a
                    data-testid="footer-phone-alt"
                    href={`tel:${PHONE_ALT_TEL}`}
                    className="transition-colors duration-300 hover:text-[#F3E5AB]"
                  >
                    {PHONE_ALT_DISPLAY}
                  </a>
                  <span className="mt-1 block text-xs text-[#A8B0BC]/70">
                    Alternate Call Number
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div data-testid="footer-hours">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#D4AF37]">
              Opening Hours
            </h3>
            <ul className="mt-7 space-y-5 text-sm font-light tracking-wide text-[#A8B0BC]">
              {HOURS.map((row) => (
                <li key={row.days} className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                  <span>
                    {row.days}
                    <span className="mt-1 block text-[#F9F6F0]/85">{row.time}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div>
            <h3 className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#D4AF37]">
              Explore
            </h3>
            <ul className="mt-7 space-y-4">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  <button
                    data-testid={`footer-link-${link.id}`}
                    onClick={() => scrollToId(link.id)}
                    className="group flex items-center gap-3 text-sm font-light tracking-wide text-[#A8B0BC] transition-colors duration-300 hover:text-white"
                  >
                    <span className="h-px w-4 bg-[#D4AF37]/40 transition-[width,background-color] duration-300 group-hover:w-7 group-hover:bg-[#D4AF37]" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <div
        aria-hidden
        className="pointer-events-none mt-14 select-none text-center font-display text-[22vw] font-semibold italic leading-[0.75] text-[#D4AF37]/[0.07]"
      >
        R&J
      </div>

      <div className="relative border-t border-[#D4AF37]/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-7 text-[11px] uppercase tracking-[0.25em] text-[#A8B0BC]/70 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <span data-testid="footer-copyright">© 2026 R&J Dry Cleaners</span>
          <span>Premium Garment Care — Yiewsley, West Drayton</span>
        </div>
      </div>
    </div>
  );
}
